import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import type {
  PaginatedResponse,
  PaginationParams,
} from '@/shared/types/api.types';

import type {
  CreateUserRequest,
  UpdateUserRequest,
  User,
} from '../types/user.types';

export const usersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query<PaginatedResponse<User>, PaginationParams>({
      query: (params) => ({
        url: '/users',
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: API_TAGS.User,
                id,
              })),
              { type: API_TAGS.Users, id: 'LIST' },
            ]
          : [{ type: API_TAGS.Users, id: 'LIST' }],
    }),

    getUserById: builder.query<User, string>({
      query: (id) => `/users/${id}`,
      providesTags: (_result, _error, id) => [{ type: API_TAGS.User, id }],
    }),

    createUser: builder.mutation<User, CreateUserRequest>({
      query: (body) => ({
        url: '/users',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: API_TAGS.Users, id: 'LIST' },
        API_TAGS.Dashboard,
      ],
    }),

    updateUser: builder.mutation<User, { id: string; data: UpdateUserRequest }>(
      {
        query: ({ id, data }) => ({
          url: `/users/${id}`,
          method: 'PATCH',
          body: data,
        }),
        invalidatesTags: (_result, _error, { id }) => [
          { type: API_TAGS.User, id },
          { type: API_TAGS.Users, id: 'LIST' },
        ],
        // Optimistic update for snappy UX
        async onQueryStarted({ id, data }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            usersApi.util.updateQueryData('getUserById', id, (draft) => {
              Object.assign(draft, data);
            }),
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        },
      },
    ),

    deleteUser: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: API_TAGS.User, id },
        { type: API_TAGS.Users, id: 'LIST' },
        API_TAGS.Dashboard,
      ],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
} = usersApi;
